import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useApi } from '../lib/apiClient';
import { Task, ApiResponse } from '../types';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  AlertTriangleIcon,
} from '@pagespeedify/ui';

const queryClient = new QueryClient();

export default function TaskDetailPageWrapper() {
  return (
    <QueryClientProvider client={queryClient}>
      <TaskDetailPage />
    </QueryClientProvider>
  );
}

function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const apiClient = useApi();
  const queryClient = useQueryClient();

  const { data: taskResponse, isLoading: isLoadingTask, isError: isErrorTask, error: taskError } = useQuery<ApiResponse<Task>, Error>({
    queryKey: ['task', id],
    queryFn: () => apiClient.get(`/tasks/${id}`),
  });

  // Render context fields based on type
  function renderContext(context: any) {
    if (!context || typeof context !== 'object') return null;
    switch (context.type) {
      case 'custom':
        return (
          <div className="mt-4">
            {context.contextDescription && (
              <div><strong>Notes:</strong> {context.contextDescription}</div>
            )}
            {context.affectedFiles && (
              <div><strong>Affected Files:</strong> <pre className="bg-muted/50 rounded p-2 text-xs whitespace-pre-wrap">{context.affectedFiles}</pre></div>
            )}
          </div>
        );
      case 'log_analysis':
        return (
          <div className="mt-4">
            <div><strong>Log Type:</strong> {context.logType}</div>
            <div><strong>Date Range:</strong> {context.dateRange?.from} to {context.dateRange?.to}</div>
            {context.errorPatterns && <div><strong>Error Patterns:</strong> {context.errorPatterns.join(', ')}</div>}
          </div>
        );
      case 'security':
        return (
          <div className="mt-4">
            <div><strong>Vulnerability:</strong> {context.vulnerability}</div>
            <div><strong>Severity:</strong> {context.severity}</div>
            {context.affectedComponents && <div><strong>Affected Components:</strong> {context.affectedComponents.join(', ')}</div>}
          </div>
        );
      default:
        return (
          <div className="mt-4 text-muted-foreground text-xs">
            <strong>Context:</strong> <pre className="bg-muted/50 rounded p-2 whitespace-pre-wrap">{JSON.stringify(context, null, 2)}</pre>
          </div>
        );
    }
  }

  if (isLoadingTask) {
    return <div className="p-8"><Skeleton className="h-96 w-full" /></div>;
  }

  if (isErrorTask || !taskResponse?.data) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <AlertTriangleIcon className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">Failed to load task</h3>
          <p className="text-muted-foreground">{taskError?.message || 'Unknown error'}</p>
        </div>
      </div>
    );
  }

  const task = taskResponse.data;

  if (!task) {
    return <div className="p-8">Task not found.</div>;
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">{task.title}</CardTitle>
          <p className="text-muted-foreground">{task.description}</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><strong>Status:</strong> {task.status}</div>
            <div><strong>Priority:</strong> {task.priority}</div>
            <div><strong>Type:</strong> {task.type}</div>
            <div><strong>Created:</strong> {new Date(task.created_at).toLocaleDateString()}</div>
            {task.github_issue_id && (
              <div className="col-span-2"><strong>GitHub Issue:</strong> <a href={task.github_issue_id} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{task.github_issue_id}</a></div>
            )}
            {task.context && renderContext(task.context)}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
